import {
  getFirstFocusableElement,
  releaseFocus,
} from '../utils/global-functions';

/**
 * Controls the header account flyout. The trigger is a real button so it is
 * operable by mouse and keyboard (Enter/Space). Desktop keeps its hover
 * affordance; keyboard/click users get an accessible, non-modal `role="dialog"`
 * flyout that manages `aria-expanded`, moves focus into the list on open, and
 * closes on Escape, outside click, or when focus leaves the flyout.
 */
export default class AccountDropdown {
  constructor() {
    this.wrapper = document.querySelector('.header__icon--account');
    this.trigger = document.querySelector('.header__account-trigger');
    this.dropdownOverlay = document.querySelector('.account-dropdown--overlay');
    this.dropdown = document.querySelector('.header__account-dropdown');

    if (!this.wrapper || !this.trigger || !this.dropdown) return;

    this.mouseOverHandler = this.mouseOver.bind(this);
    this.mouseOutHandler = this.mouseOut.bind(this);

    this.bindEvents();
    this.init();
    window.addEventListener('resize', this.init.bind(this));
  }

  /**
   * Whether the flyout is currently open via the keyboard/click toggle.
   * @returns {boolean}
   */
  get isOpen() {
    return this.dropdown.classList.contains('open');
  }

  /**
   * Reveal the flyout, reflect its state for assistive technology, and move
   * focus to the first option. The flyout is non-modal, so page scrolling is
   * intentionally left enabled.
   */
  open() {
    this.dropdown.classList.add('open');
    this.dropdownOverlay.classList.add('open');
    this.trigger.setAttribute('aria-expanded', 'true');

    const firstOption = getFirstFocusableElement(this.dropdown);
    if (firstOption) firstOption.focus();
  }

  /**
   * Hide the flyout and reflect its state for assistive technology.
   * @param {{ returnFocus?: boolean }} [options] - Return focus to the trigger.
   */
  close({ returnFocus = false } = {}) {
    this.dropdown.classList.remove('open');
    this.dropdownOverlay.classList.remove('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus) releaseFocus(this.trigger);
  }

  toggle() {
    if (this.isOpen) this.close({ returnFocus: true });
    else this.open();
  }

  /**
   * Wire up the trigger and the close interactions. These listeners are
   * viewport-independent so the flyout is keyboard operable at every breakpoint;
   * `init()` layers the desktop-only hover affordance on top.
   */
  bindEvents() {
    // Fires for mouse click and for keyboard Enter/Space on the button.
    this.trigger.addEventListener('click', () => this.toggle());

    this.wrapper.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close({ returnFocus: true });
      }
    });

    // Closing when focus leaves the flyout keeps this non-modal popup from
    // trapping the keyboard: tabbing past the last option moves focus to the
    // next control and dismisses the flyout; shift-tabbing past the first
    // option returns to the trigger.
    //
    // This auto-close is intentionally KEYBOARD-ONLY. On touch, tapping an
    // option blurs the focused element (firing `focusout`) before the `click`
    // that navigates — and that click can arrive in a later task, so closing
    // here (even deferred a frame) sets the option's ancestor to `display:
    // none` before the click lands, swallowing the navigation. Pointer
    // dismissal is already covered by the outside-click and Escape handlers,
    // so we skip the focus-out close whenever the focus change was driven by a
    // pointer, and rely on `relatedTarget` for genuine keyboard tab-outs.
    let pointerInteraction = false;
    this.wrapper.addEventListener('pointerdown', () => {
      pointerInteraction = true;
    });

    this.wrapper.addEventListener('focusout', (event) => {
      if (pointerInteraction) {
        pointerInteraction = false;
        return;
      }
      if (this.isOpen && !this.wrapper.contains(event.relatedTarget)) {
        this.close();
      }
    });

    document.addEventListener('click', (event) => {
      if (this.isOpen && !this.wrapper.contains(event.target)) {
        this.close();
      }
    });
  }

  mouseOver() {
    this.dropdownOverlay.classList.add('open');
  }

  mouseOut() {
    this.dropdownOverlay.classList.remove('open');
  }

  /**
   * Toggle the desktop-only hover affordance. Below the breakpoint the trigger's
   * click/keyboard toggle is the only way to open the flyout.
   */
  init() {
    if (window.innerWidth <= 1099) {
      this.wrapper.removeEventListener('mouseover', this.mouseOverHandler);
      this.wrapper.removeEventListener('mouseout', this.mouseOutHandler);
    } else {
      this.wrapper.addEventListener('mouseover', this.mouseOverHandler);
      this.wrapper.addEventListener('mouseout', this.mouseOutHandler);
    }
  }
}
